/**
 * Table row for a pair.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, User, Calendar, FileText, XCircle } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PairRecord } from '@/types/remote/pair.types';
import {
  canAddOneOffSession,
  deriveDisplay,
  getAddOneOffSessionTooltip,
  getArchivedPairDissociateTooltip,
  getDissociateTooltip,
} from './pair-table-utils';

/** Props for {@link PairRow}. */
interface PairRowProps {
  pair: PairRecord;
  onView: () => void;
  onEdit?: () => void;
  onDissociate?: () => void;
  onViewSessions?: () => void;
  onAddOneOffSession?: () => void;
}

/** Remote pair table row (people, KPIs, dissociate). */
export const PairRow: React.FC<PairRowProps> = ({
  pair,
  onView,
  onEdit,
  onDissociate,
  onViewSessions,
  onAddOneOffSession,
}) => {
  const navigate = useNavigate();
  const kpis = pair.kpis;
  const {
    isArchived,
    reportsWritten,
    reportsTotal,
    sessionsPlanned,
    sessionsCompleted,
    statusCfg,
    progCfg,
    tutorSituationCfg,
    studentSituationCfg,
    editTooltip,
  } = deriveDisplay(pair);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <GraduationCap className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <button
              type="button"
              className="font-medium text-foreground hover:underline truncate text-left"
              onClick={(e) => {
                e.stopPropagation();
                void navigate(`/tutors?view=${pair.tutorUserId}`);
              }}
            >
              {pair.tutorName}
            </button>
            {tutorSituationCfg && (
              <div className="mt-0.5">
                <LabelBadge
                  label={tutorSituationCfg.label}
                  icon={tutorSituationCfg.icon}
                  variant={tutorSituationCfg.badgeVariant}
                  color={tutorSituationCfg.badgeColor}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <button
              type="button"
              className="font-medium text-foreground hover:underline truncate text-left"
              onClick={(e) => {
                e.stopPropagation();
                void navigate(`/students?view=${pair.studentUserId}`);
              }}
            >
              {pair.studentName}
            </button>
            {studentSituationCfg && (
              <div className="mt-0.5">
                <LabelBadge
                  label={studentSituationCfg.label}
                  icon={studentSituationCfg.icon}
                  variant={studentSituationCfg.badgeVariant}
                  color={studentSituationCfg.badgeColor}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
      </td>

      <td>
        <div className="flex flex-col items-start gap-1">
          <LabelBadge
            label={statusCfg.label}
            icon={statusCfg.icon}
            variant={statusCfg.badgeVariant}
            color={statusCfg.badgeColor}
            size="sm"
          />
          {progCfg && (
            <LabelBadge label={progCfg.label} variant="outlined" color={progCfg.color} size="sm" />
          )}
        </div>
      </td>

      <td>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex">
                <LabelBadge
                  label={`${sessionsCompleted}/${sessionsPlanned} séances`}
                  icon={Calendar}
                  variant="ghost"
                  color="var(--color-2-nuance-1)"
                  size="sm"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs space-y-1">
                <p>
                  Séances réalisées / planifiées : {sessionsCompleted}/{sessionsPlanned}
                </p>
                {kpis && <p>Séances annulées : {kpis.sessionsCancelled}</p>}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>

      <td>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex">
                <LabelBadge
                  label={`${reportsWritten}/${reportsTotal} CR`}
                  icon={FileText}
                  variant="ghost"
                  color="var(--color-1-nuance-1)"
                  size="sm"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs space-y-1">
                <p>
                  Comptes rendus rédigés : {reportsWritten}/{reportsTotal}
                </p>
                {kpis?.averageScore !== null && kpis?.averageScore !== undefined && (
                  <p>Note moyenne : {kpis.averageScore.toFixed(1)}/5</p>
                )}
                {pair.managedBy && <p>Géré par {pair.managedBy.fullName}</p>}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView()} />

          {onEdit && (
            <Button
              preset="row-edit"
              onClick={() => onEdit()}
              disabled={isArchived}
              tooltipText={editTooltip}
            />
          )}

          {onViewSessions && <Button preset="row-view-sessions" onClick={() => onViewSessions()} />}

          {onAddOneOffSession && (
            <Button
              preset="row-add-one-off-session"
              onClick={() => onAddOneOffSession()}
              disabled={!canAddOneOffSession(pair)}
              tooltipText={getAddOneOffSessionTooltip(pair)}
            />
          )}

          {onDissociate &&
            (isArchived ? (
              <Button
                preset="row-delete"
                icon={XCircle}
                disabled
                tooltipText={getArchivedPairDissociateTooltip()}
              />
            ) : (
              <Button
                preset="row-delete"
                icon={XCircle}
                tooltipText={getDissociateTooltip()}
                onClick={() => onDissociate()}
              />
            ))}
        </div>
      </td>
    </TableRow>
  );
};
